import type { CanvasPoint, CanvasTapTargets, FeatureOfferPort } from "@slot/game-shell";
import type { Log } from "@slot/presentation";
import { SECRET_BONUS_SYMBOL_ID } from "../../assets/mergeLadder";
import type { SecretBonusOffer, SecretBonusTarget } from "./replay/PresentSecretBonusActionHandler";

/** The hand over the Secret Bonus cell, as the presenter drives it: methods only (plan §2.3). */
export interface SecretBonusPort {
  /** The hand on the cell, pointing at the symbol and tapping, until `hide`. */
  show(positionIndex: number): void;
  hide(): void;
  /** True when a point in the canvas's global space falls on the cell. */
  hits(globalPoint: CanvasPoint, positionIndex: number): boolean;
}

/**
 * The Secret Bonus (decision 51): the server lands its symbol on a base spin's last board and names the offer; from
 * then until the next spin, a tap on its cell opens that offer's confirm — the purchase is the shell's (its price, its
 * checks, its Back), and the offer has no card on the purchase panel. A Back leaves the offer as it was; the next spin
 * (the player's, any spin's boundary, a recovery) takes it away with the board.
 */
export class SecretBonusPresenter implements SecretBonusTarget {
  private offer: SecretBonusOffer | null = null;

  public constructor(
    private readonly deps: {
      view: SecretBonusPort;
      offers: FeatureOfferPort;
      taps: CanvasTapTargets;
      /** The symbol a cell shows now (the grid's), to check the server's cell. */
      symbolAt: (positionIndex: number) => string | null;
      log: Log;
    },
  ) {
    deps.taps.add((point) => this.onTap(point));
  }

  public async presentSecretBonus(offer: SecretBonusOffer): Promise<void> {
    const shown = this.deps.symbolAt(offer.positionIndex);
    if (shown !== SECRET_BONUS_SYMBOL_ID) {
      this.deps.log(`[SecretBonus] cell ${offer.positionIndex} shows "${shown ?? "nothing"}", not the Secret Bonus: no offer`);
      return;
    }
    this.offer = offer;
    this.deps.view.show(offer.positionIndex);
    this.deps.log(`[SecretBonus] offered on cell ${offer.positionIndex}: ${offer.offerIdentifier}`);
  }

  /** The board goes: the player's spin, a spin's boundary, a recovery. */
  public clear(): void {
    if (!this.offer) return;
    this.offer = null;
    this.deps.view.hide();
  }

  /** A tap on the offer's cell is the offer's, whether or not its confirm can open now; any other tap passes on. */
  private onTap(point: CanvasPoint): boolean {
    const offer = this.offer;
    if (!offer || !this.deps.view.hits(point, offer.positionIndex)) return false;
    const opened = this.deps.offers.promptOffer(offer.offerIdentifier);
    this.deps.log(`[SecretBonus] tapped: ${opened ? "the offer opens" : "the offer cannot open now"}`);
    return true;
  }
}
